import React, { useEffect, useRef, useState } from 'react';

import { Check, X, Loader2 } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { useTheme } from '../../contexts/ThemeContext';



function getPricingCardColors(isDark: boolean) {

  return {

    basicBg: isDark ? '#1d1d1d' : '#ffffff',

    basicBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',

    heading: isDark ? '#ffffff' : '#111827',

    subtitle: isDark ? '#9ca3af' : '#6b7280',

    body: isDark ? '#e5e7eb' : '#374151',

    muted: isDark ? '#9ca3af' : '#6b7280',

    premiumBg: isDark

      ? 'linear-gradient(135deg, #121212 0%, #221a38 25%, #121212 50%, #1a2847 75%, #121212 100%)'

      : 'linear-gradient(135deg, #f3eefb 0%, #e8e6ff 40%, #fce8f6 70%, #fff0f9 100%)',

    premiumShadow: isDark

      ? undefined

      : '0 4px 20px rgba(124, 118, 255, 0.08)',

    ctaBorder: isDark ? '#374151' : '#d1d5db',

    ctaText: isDark ? '#ffffff' : '#111827',

  };

}



export const PREMIUM_STRIPE_LINKS = {

  monthly: 'https://buy.stripe.com/eVqdRa0js9eWe246Ia4800Y',

  yearly: 'https://buy.stripe.com/aFa00k4zIcr84ru4A24800W',

} as const;



const PREMIUM_ANNUAL_TOTAL = '228€';



export const PREMIUM_PRICES = {

  monthly: '29€',

  yearly: '19€',

};



const toAmount = (value: string) => parseFloat(value.replace('€', '').replace(',', '.'));

/** Économie réelle de l'annuel, dérivée des prix pour ne jamais dériver : 29×12 − 228. */
export const PREMIUM_ANNUAL_SAVINGS = `${Math.round(
  toAmount(PREMIUM_PRICES.monthly) * 12 - toAmount(PREMIUM_ANNUAL_TOTAL),
)}€`;

/** Prix annuel « plein » (barré) : 12 mois au tarif mensuel, soit 29×12. */
export const PREMIUM_ANNUAL_FULL = `${Math.round(toAmount(PREMIUM_PRICES.monthly) * 12)}€`;

/** Features Premium listées en entier sur desktop ; le mobile n'en montre que 4. */
const PREMIUM_MOBILE_FEATURE_COUNT = 4;
/** Nombre affiché dans le « +N autres fonctionnalités incluses » du mobile (demandé explicitement). */
const PREMIUM_OTHER_FEATURES_COUNT = 30;

/** Rose de la charte — bordure de la carte Premium et pointillés du « +N autres ». */
const PREMIUM_ACCENT = '#E0219A';



const AnimatedPrice: React.FC<{ value: string; interval: 'monthly' | 'yearly' }> = ({ value, interval }) => {

  const [displayValue, setDisplayValue] = useState(value);

  const prevIntervalRef = useRef(interval);



  useEffect(() => {

    if (prevIntervalRef.current === interval) {

      setDisplayValue(value);

      return;

    }



    const startValue = parseFloat(displayValue.replace('€', '').replace(',', '.'));

    const endValue = parseFloat(value.replace('€', '').replace(',', '.'));

    const duration = 600;

    const startTime = Date.now();



    const animate = () => {

      const elapsed = Date.now() - startTime;

      const progress = Math.min(elapsed / duration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + (endValue - startValue) * easeOut;

      setDisplayValue(currentValue.toFixed(2).replace('.', ',') + '€');



      if (progress < 1) {

        requestAnimationFrame(animate);

      } else {

        setDisplayValue(value);

        prevIntervalRef.current = interval;

      }

    };



    requestAnimationFrame(animate);

  }, [value, interval, displayValue]);



  return <>{displayValue}</>;

};



export interface PricingPlanCardsProps {

  billingInterval: 'monthly' | 'yearly';

  loadingUpgrade?: boolean;

  basicCtaLabel: string;

  premiumCtaLabel: string;

  onBasicClick: () => void;

  onPremiumClick: () => void;

  basicDisabled?: boolean;

  premiumDisabled?: boolean;

  hideBasicCta?: boolean;

  premiumOnly?: boolean;

  /** Rend uniquement la carte gratuite : /pricing affiche sa propre carte Premium. */
  basicOnly?: boolean;

  /**
   * Position du CTA gratuit dans la carte. 'bottom' (défaut, landing/modal)
   * le laisse sous la liste de features. 'top' le remonte juste sous le prix,
   * pour aligner sa hauteur avec le CTA de la carte Premium sur /pricing.
   */
  basicCtaPlacement?: 'top' | 'bottom';

  themeAware?: boolean;

  onBasicHover?: (hover: boolean) => void;

  onPremiumHover?: (hover: boolean) => void;

  basicCtaFootnote?: string;

  premiumCtaFootnote?: string;

  /**
   * 'modal' : mise en page dédiée au PricingModal — colonne de comparaison
   * « GRATUIT » à gauche sur desktop uniquement, et liste de features
   * condensée (4 + « +5 autres ») sur mobile. 'default' = /pricing, inchangé.
   */
  variant?: 'default' | 'modal';

  /** Petite ligne affichée à l'intérieur du bouton, sous le libellé principal. */
  premiumCtaSubLabel?: string;

}



export function PricingPlanCards({

  billingInterval,

  loadingUpgrade = false,

  basicCtaLabel,

  premiumCtaLabel,

  onBasicClick,

  onPremiumClick,

  basicDisabled = false,

  premiumDisabled = false,

  hideBasicCta = false,

  premiumOnly = false,

  basicOnly = false,

  basicCtaPlacement = 'bottom',

  themeAware = false,

  onBasicHover,

  onPremiumHover,

  basicCtaFootnote,

  premiumCtaFootnote,

  variant = 'default',

  premiumCtaSubLabel,

}: PricingPlanCardsProps) {

  const { theme } = useTheme();

  const isDark = themeAware ? theme === 'dark' : true;

  const { t } = useTranslation();
  const colors = getPricingCardColors(isDark);

  const isModal = variant === 'modal';

  const premiumCardWidth = premiumOnly ? '400px' : '490px';



  /* Colonne « GRATUIT » du modal : rappel de ce que l'offre gratuite contient
     et, barré, de ce qu'elle ne contient pas. Desktop uniquement — sur mobile
     elle ferait défiler l'utilisateur avant d'atteindre l'offre payante. */
  const freeComparisonCard = (
    <div
      className="hidden lg:flex flex-col rounded-2xl p-7 self-center flex-shrink-0"
      style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
        width: '248px',
        minHeight: '430px',
      }}
    >
      <span className="text-[12px] font-bold uppercase mb-4" style={{ color: colors.muted, letterSpacing: '0.1em' }}>
        {t('layout.pm_free_label')}
      </span>

      <span className="text-[2.5rem] font-extrabold leading-none mb-2" style={{ color: colors.heading }}>
        {t('layout.pm_free_price')}
      </span>

      <span className="text-[13px] mb-5" style={{ color: colors.muted }}>
        {t('layout.pm_free_current')}
      </span>

      <ul className="pm-plain-list space-y-3.5 text-[13.5px] leading-snug pt-5" style={{ borderTop: `1px solid ${colors.basicBorder}` }}>
        {[1, 2, 3, 4].map((i) => (
          <li key={`locked-${i}`} style={{ color: colors.muted, textDecoration: 'line-through', opacity: 0.7 }}>
            {t(`layout.pm_free_locked_${i}`)}
          </li>
        ))}
        {[1, 2].map((i) => (
          <li key={`included-${i}`} style={{ color: colors.muted }}>
            {t(`layout.pm_free_included_${i}`)}
          </li>
        ))}
      </ul>
    </div>
  );



  /* Liste condensée mobile : 4 features détaillées (titre + bénéfice) puis le
     reste agrégé. La liste desktop complète tiendrait sur 2 écrans de scroll. */
  const premiumMobileFeatures = (
    <ul className="pm-plain-list pm-mobile-only space-y-3.5 text-left my-5">
      {Array.from({ length: PREMIUM_MOBILE_FEATURE_COUNT }, (_, idx) => idx + 1).map((i) => (
        <li key={i}>
          <p className="text-[13.5px] font-semibold leading-snug" style={{ color: colors.heading }}>
            {t(`layout.pm_m_f${i}_title`)}
          </p>
          <p className="text-[12px] leading-snug mt-0.5" style={{ color: colors.muted }}>
            {t(`layout.pm_m_f${i}_desc`)}
          </p>
        </li>
      ))}
      <li className="pt-1">
        <span
          className="text-[13px] font-semibold"
          style={{
            color: PREMIUM_ACCENT,
            borderBottom: `1px dotted ${PREMIUM_ACCENT}`,
            paddingBottom: '2px',
            display: 'inline-block',
          }}
        >
          {t('layout.pm_m_more', { count: PREMIUM_OTHER_FEATURES_COUNT })}
        </span>
      </li>
    </ul>
  );

  /* Bouton Premium : sur /pricing (et donc dans le modal, qui doit reprendre
     exactement ce placement) il est juste sous le prix, avant la liste de
     features — jamais collé au bas de la carte comme sur le comparatif
     desktop/landing par défaut, où le flex-1 l'aligne avec le bouton Basic. */
  const premiumCtaButton = (
    <button
      type="button"
      className={
        isModal
          // Le conteneur prix+note a déjà mb-4 : pas de mt ici pour ne pas doubler
          // l'espace au-dessus, mb-4 identique en dessous pour un espacement symétrique.
          ? 'premium-cta-button mb-4 w-full bento-button py-3 px-5 relative z-10 text-sm'
          : 'premium-cta-button mt-7 w-full bento-button py-3 px-5 rounded-xl relative z-10 text-sm'
      }
      style={{
        background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
        ...(isModal ? { borderRadius: '9999px' } : {}),
      }}
      onClick={onPremiumClick}
      onMouseEnter={() => onPremiumHover?.(true)}
      onMouseLeave={() => onPremiumHover?.(false)}
      disabled={loadingUpgrade || premiumDisabled}
    >
      {loadingUpgrade ? (
        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
      ) : premiumCtaSubLabel ? (
        <span className="flex flex-col items-center leading-tight">
          <span>{premiumCtaLabel}</span>
          <span className="text-[11px] font-normal mt-0.5" style={{ opacity: 0.85 }}>{premiumCtaSubLabel}</span>
        </span>
      ) : (
        premiumCtaLabel
      )}
    </button>
  );

  const basicCtaBlock = !hideBasicCta && (

    <>

      <button

        type="button"

        className="pricing-cta-button w-full py-3 px-5 rounded-xl border transition-all font-medium text-sm"

        style={{

          borderRadius: basicCtaPlacement === 'top' ? '9999px' : undefined,

          borderColor: colors.ctaBorder,

          color: basicDisabled ? colors.muted : colors.ctaText,

          background: 'transparent',

          cursor: basicDisabled ? 'default' : 'pointer',

          opacity: basicDisabled ? 0.7 : 1,

        }}

        onClick={onBasicClick}

        onMouseEnter={() => onBasicHover?.(true)}

        onMouseLeave={() => onBasicHover?.(false)}

        disabled={loadingUpgrade || basicDisabled}

      >

        {loadingUpgrade ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : basicCtaLabel}

      </button>

      {basicCtaFootnote && (

        <p className="mt-2 text-[11px] text-center leading-snug" style={{ color: colors.muted }}>

          {basicCtaFootnote}

        </p>

      )}

    </>

  );

  const basicCard = (

    <div className="basic-plan-card relative p-6 rounded-2xl flex flex-col lg:self-center" style={{ background: colors.basicBg, border: `1px solid ${colors.basicBorder}`, maxWidth: '450px', margin: '0 auto', width: '100%', zIndex: 2 }}>

      <div className="flex-1">

        <h3 className="text-[1.7rem] font-bold mb-2 text-center" style={{ color: colors.heading, animation: 'fadeInUp 0.8s ease-out 0s both' }}>{t('landing.plans.basic')}</h3>

        <p className="text-[13px] text-center mb-4" style={{ color: colors.subtitle, animation: 'fadeInUp 0.8s ease-out 0.1s both' }}>{t('landing.plans.basic_subtitle')}</p>

        <div className="mb-5" style={{ animation: 'fadeInUp 0.8s ease-out 0.15s both' }}>

          <div className="flex items-baseline gap-1 justify-center">

            <span className="text-[3rem] font-extrabold leading-none" style={{ color: colors.heading }}>{t('landing.plans.free')}</span>

          </div>

          <div className="text-[12px] text-gray-400 mt-1 text-center">{t('landing.plans.no_card')}</div>

        </div>

        {/* Pas de bouton à afficher (ex : essai Premium actif) : aucun espace
            réservé, la liste de features remonte juste sous le prix. */}
        {basicCtaPlacement === 'top' && !hideBasicCta && (

          <div className="mb-5">{basicCtaBlock}</div>

        )}

        <ul className="space-y-2.5 text-[13px] leading-tight">

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.2s both' }}>

            <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

            <span style={{ color: colors.body, fontWeight: 600 }}>{t('landing.plans.basic_f1')}</span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.3s both' }}>

            <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

            <span style={{ color: colors.body, fontWeight: 600 }}>{t('landing.plans.basic_f2')}</span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.4s both' }}>

            <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

            <span style={{ color: colors.body, fontWeight: 600 }}>{t('landing.plans.basic_f3')}</span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.45s both' }}>

            <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

            <span style={{ color: colors.body, fontWeight: 600 }}>{t('landing.plans.basic_f4')}</span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.5s both' }}>

            <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

            <span style={{ color: colors.body, fontWeight: 600 }}>{t('landing.plans.basic_f5')} <span style={{ color: colors.muted, fontWeight: 'normal', fontSize: '12px' }}>{t('landing.plans.basic_f5_note')}</span></span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.6s both' }}>

            <X className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: colors.muted }} />

            <span style={{ color: colors.muted }}>{t('landing.plans.basic_f6')}</span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.7s both' }}>

            <X className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: colors.muted }} />

            <span style={{ color: colors.muted }}>{t('landing.plans.basic_f7')}</span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.8s both' }}>

            <X className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: colors.muted }} />

            <span style={{ color: colors.muted }}>{t('landing.plans.basic_f8')}</span>

          </li>

          <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.9s both' }}>

            <X className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: colors.muted }} />

            <span style={{ color: colors.muted }}>{t('landing.plans.basic_f9')}</span>

          </li>

        </ul>

      </div>

      {basicCtaPlacement !== 'top' && (

        hideBasicCta ? (

          <div className="mt-7 h-[46px]" aria-hidden="true" />

        ) : (

          <div className="mt-7">{basicCtaBlock}</div>

        )

      )}

    </div>

  );



  const premiumCard = (

    <div className="relative" style={{ maxWidth: premiumCardWidth, margin: '0 auto', width: '100%', minHeight: premiumOnly ? undefined : '660px', zIndex: 1 }}>

      {!premiumOnly && (
      <span

        className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"

        style={{

          top: 0,

          padding: '5px 16px',

          borderRadius: '999px',

          fontSize: '0.75rem',

          fontWeight: 600,

          color: '#ffffff',

          background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',

          whiteSpace: 'nowrap',

        }}

      >

        {t('landing.plans.recommended')}

      </span>
      )}

      <div
        className="relative p-6 rounded-2xl flex flex-col h-full"
        style={{
          background: colors.premiumBg,
          ...(colors.premiumShadow ? { boxShadow: colors.premiumShadow } : {}),
          ...(isModal ? { border: `1.5px solid ${PREMIUM_ACCENT}` } : {}),
        }}
      >

        <div className="flex-1 flex flex-col">

          <h3 className="text-[1.65rem] font-bold mb-2 text-center" style={{ animation: 'fadeInUp 0.8s ease-out 0s both' }}>

            <span

              style={isModal ? { color: isDark ? '#ffffff' : '#000000' } : {

                backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',

                WebkitBackgroundClip: 'text',

                WebkitTextFillColor: 'transparent',

                backgroundClip: 'text',

              }}

            >

              {t('landing.plans.premium')}

            </span>

          </h3>

          <p className="text-[13px] text-center mb-3" style={{ color: colors.subtitle, animation: 'fadeInUp 0.8s ease-out 0.1s both' }}>{t('landing.plans.premium_subtitle')}</p>

          <div className="mb-4" style={{ animation: 'fadeInUp 0.8s ease-out 0.15s both' }}>

            <div className="flex items-baseline gap-2 justify-center flex-wrap">

              {/* Prix barré déplacé sous le prix (cf. bloc annuel plus bas), comme sur /pricing :
                  il ne barre plus le prix effectivement payé sur cette même ligne. */}
              <span

                className={isModal ? 'text-[2.35rem] sm:text-[3rem] font-extrabold leading-none' : 'text-[3rem] font-extrabold leading-none'}

                style={isModal ? { color: colors.heading } : {

                  backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',

                  WebkitBackgroundClip: 'text',

                  WebkitTextFillColor: 'transparent',

                  backgroundClip: 'text',

                }}

              >

                <AnimatedPrice value={PREMIUM_PRICES[billingInterval === 'yearly' ? 'yearly' : 'monthly']} interval={billingInterval} />

              </span>

              <span className={`${isModal ? 'text-base sm:text-xl' : 'text-xl'} font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('landing.plans.per_month')}</span>

            </div>

            {isModal ? (

              billingInterval === 'yearly' ? (

                <>

                  {/* Même détail que la carte Premium de /pricing : prix plein barré
                      puis prix annuel réel, sur une ligne. */}
                  <p className="flex items-baseline justify-center gap-2 text-center" style={{ fontSize: '0.85rem', margin: '0.5rem 0 0' }}>

                    <span style={{ color: colors.muted, opacity: 0.65, textDecoration: 'line-through' }}>

                      {PREMIUM_ANNUAL_FULL}

                    </span>

                    <span style={{ color: colors.heading, fontWeight: 600 }}>

                      {PREMIUM_ANNUAL_TOTAL} {t('layout.pm_annual_per_year_suffix')}

                    </span>

                  </p>

                  <p className="text-center" style={{ color: colors.muted, fontSize: '0.72rem', margin: '0.3rem 0 0' }}>

                    {PREMIUM_ANNUAL_TOTAL}{t('layout.pm_annual_note_suffix')}

                  </p>

                </>

              ) : (

                <p className="text-center" style={{ color: colors.body, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>

                  {t('layout.pm_monthly_note', { amount: PREMIUM_ANNUAL_SAVINGS })}

                </p>

              )

            ) : (

              billingInterval === 'yearly' && (

                <div className={`text-[12px] mt-1 text-center ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('landing.plans.billed_annually', { total: PREMIUM_ANNUAL_TOTAL })}</div>

              )

            )}

          </div>

          {isModal && premiumCtaButton}

          {isModal && premiumMobileFeatures}

          <ul className={`space-y-4 text-[13px] leading-tight${isModal ? ' pm-plain-list hidden lg:block' : ' my-auto'}`}>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.2s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}>

                <span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f1_strong')}</span>

                <span style={{ display: 'block', color: colors.muted, fontWeight: 'normal', fontSize: '12px', marginTop: '2px' }}>{t('landing.plans.premium_f1_note')}</span>

              </span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.3s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f2_strong')}</span> {t('landing.plans.premium_f2_rest')}</span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.4s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f3_strong')}</span> {t('landing.plans.premium_f3_rest')}</span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.5s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f4_strong')}</span> {t('landing.plans.premium_f4_rest')}</span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.6s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f5_strong')}</span> {t('landing.plans.premium_f5_rest')}</span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.7s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f6_strong')}</span> {t('landing.plans.premium_f6_rest')}</span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.8s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f7_strong')}</span> {t('landing.plans.premium_f7_rest')}</span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 0.9s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f8_strong')}</span> {t('landing.plans.premium_f8_rest')}</span>

            </li>

            <li className="flex items-start gap-2.5" style={{ animation: 'fadeInUp 0.8s ease-out 1s both' }}>

              <Check className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />

              <span style={{ color: colors.body }}><span style={{ fontWeight: 600 }}>{t('landing.plans.premium_f9_strong')}</span> {t('landing.plans.premium_f9_rest')}</span>

            </li>

          </ul>

        </div>

        {/* En modal, le bouton a déjà été rendu plus haut, juste sous le prix
            (cf. premiumCtaButton) — exactement le placement de /pricing. */}
        {!isModal && premiumCtaButton}

        {premiumCtaFootnote && (

          <p className="mt-2 text-[11px] text-center leading-snug" style={{ color: colors.muted }}>

            {premiumCtaFootnote}

          </p>

        )}

      </div>

    </div>

  );



  if (basicOnly) {

    return (

      <div className="mx-auto w-full" style={{ position: 'relative', zIndex: 2, maxWidth: '450px' }}>

        {basicCard}

      </div>

    );

  }



  if (premiumOnly) {

    if (isModal) {

      return (

        <div className="mx-auto w-full flex items-center justify-center gap-6" style={{ position: 'relative', zIndex: 1, maxWidth: '690px' }}>

          {freeComparisonCard}

          <div className="w-full" style={{ maxWidth: premiumCardWidth }}>{premiumCard}</div>

        </div>

      );

    }

    return (

      <div className="mx-auto w-full" style={{ position: 'relative', zIndex: 1, maxWidth: premiumCardWidth }}>

        {premiumCard}

      </div>

    );

  }



  return (

    <div className="mt-6 sm:mt-8 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto lg:items-center" style={{ position: 'relative', zIndex: 1 }}>

      {basicCard}

      {premiumCard}

    </div>

  );

}


